import posthog from "posthog-js";

const POSTHOG_KEY = import.meta.env.REACT_APP_POSTHOG_KEY;
const POSTHOG_HOST = import.meta.env.REACT_APP_POSTHOG_HOST || "https://us.i.posthog.com";

let initialized = false;
let consented = false;

// A visitor can sign in before they answer the cookie banner, so the last identity is
// held here and replayed the moment consent arrives. Without it, anyone who accepts
// *after* logging in stays anonymous for the rest of the session.
let pendingUser = null;

/**
 * Start analytics. Called only from the consent layer's onAccept — never at boot.
 * Until a visitor opts in, PostHog is not initialised and writes no cookie and no
 * localStorage entry.
 */
export function grantConsent() {
  consented = true;
  if (!POSTHOG_KEY) return;

  if (initialized) {
    posthog.opt_in_capturing();
  } else {
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      capture_pageview: true,
      // Clicks are counted, never read. Unmasked, autocapture sends a button's
      // text and attributes such as aria-label, and a button in Hans is often
      // a student's name. The privacy policy says PostHog never receives
      // students' details, and iOS captures screens, not touches.
      autocapture: true,
      mask_all_text: true,
      mask_all_element_attributes: true,
      // Session replay would record the page itself. The cookie-consent spec
      // lists it as absent; this keeps it off whatever the PostHog project's
      // remote settings say.
      disable_session_recording: true,
    });
    initialized = true;
  }

  if (pendingUser) identify(pendingUser);
}

/**
 * Stop analytics and clear what PostHog has stored. Cookie Control sweeps the cookies
 * on its own, but opting out is what halts capture already running on the current page —
 * without it a revoke would not take effect until the next navigation.
 */
export function revokeConsent() {
  consented = false;
  pendingUser = null;
  if (!initialized) return;
  posthog.opt_out_capturing();
  posthog.reset();
}

export function hasConsent() {
  return consented;
}

/**
 * Identify by account id and plan only.
 *
 * The account id is `user_id`, the field /auth/me carries (it has no `_id` or
 * `id`), as in mobile/src/services/analyticsService.js. Only the instructor
 * shell calls this; a student portal session stays anonymous, as on iOS.
 *
 * Name and email are deliberately NOT sent. The privacy policy tells visitors that
 * PostHog never receives them, and this function is the only thing keeping that true —
 * adding a field here silently makes that copy a false statement.
 */
export function identify(user) {
  if (!user?.user_id) return;
  pendingUser = user;
  if (!initialized || !consented) return;
  posthog.identify(user.user_id, {
    subscription_tier: user.subscription_tier,
  });
}

export function reset() {
  pendingUser = null;
  if (!initialized) return;
  posthog.reset();
}

export function track(event, properties) {
  if (!initialized || !consented) return;
  posthog.capture(event, properties);
}
